import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Subscription } from 'rxjs';
import { DialogComponent } from '../../modal/dialog/dialog.component';
import { DialogModule } from '../../modal/dialog/dialog.module';

const BRANCHING_OPTION_MIN = 2;
const BRANCHING_OPTION_MAX = 5;

@Component({
  selector: 'app-form-branching',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    AngularSvgIconModule,
    DialogModule,
  ],
  templateUrl: './form-branching.component.html',
  styleUrl: './form-branching.component.scss',
})
export class FormBranchingComponent implements OnDestroy {
  private _languages: string[] = [];
  private _options: UntypedFormArray | null = null;
  private subscriptions: Subscription[] = [];

  readonly minOptions = BRANCHING_OPTION_MIN;
  readonly maxOptions = BRANCHING_OPTION_MAX;

  constructor(
    private fb: UntypedFormBuilder,
    private dialog: MatDialog,
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  @Input()
  set languages(value: string[]) {
    this._languages = value ?? [];
    this.ensureDescriptions();
  }

  get languages(): string[] {
    return this._languages;
  }

  @Input()
  set options(value: UntypedFormArray | null) {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];

    this._options = value;
    this.ensureOptionCount();
    this.ensureDescriptions();
    this.setupDefaultOptionListeners();
  }

  get options(): UntypedFormArray | null {
    return this._options;
  }

  @Input() submitted = false;

  get optionIndexes(): number[] {
    const length = this.options?.length ?? BRANCHING_OPTION_MIN;
    return Array.from({ length }, (_, idx) => idx);
  }

  get canAddOption(): boolean {
    return (this.options?.length ?? 0) < BRANCHING_OPTION_MAX;
  }

  get canRemoveOption(): boolean {
    return (this.options?.length ?? 0) > BRANCHING_OPTION_MIN;
  }

  trackByLanguage(_: number, lang: string): string {
    return lang;
  }

  trackByIndex(_: number, index: number): number {
    return index;
  }

  addOption(): void {
    if (!this.options || !this.canAddOption) {
      return;
    }

    this.options.push(this.createOptionGroup(this.options.length));
    this.ensureDescriptions();
    this.setupDefaultOptionListeners();
  }

  confirmRemoveOption(index: number): void {
    if (!this.options || !this.canRemoveOption) {
      return;
    }

    const dialogRef = this.dialog.open(DialogComponent, {
      data: {
        title: 'DELETE_BRANCHING_OPTION',
        body: 'DELETE_BRANCHING_OPTION',
        buttons: [
          { title: 'DELETE', action: 'DELETE', color: 'warn' },
          { title: 'CANCEL', action: 'CANCEL', color: '' },
        ],
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'DELETE') {
        this.removeOption(index);
      }
    });
  }

  removeOption(index: number): void {
    if (!this.options || !this.canRemoveOption) {
      return;
    }

    this.options.removeAt(index);
    this.optionIndexes.forEach((optionIndex) => {
      const optionGroup = this.options?.at(optionIndex) as UntypedFormGroup;
      optionGroup?.get('order')?.setValue(optionIndex, { emitEvent: false });
    });
    this.setupDefaultOptionListeners();
  }

  getOptionControl(
    optionIndex: number,
    controlName: string
  ): UntypedFormControl | null {
    const optionGroup = this.ensureOptionGroup(optionIndex);
    return (optionGroup?.get(controlName) as UntypedFormControl) ?? null;
  }

  getDescriptionControl(
    optionIndex: number,
    language: string
  ): UntypedFormControl | null {
    const optionGroup = this.ensureOptionGroup(optionIndex);
    if (!optionGroup) {
      return null;
    }
    const descriptions = optionGroup.get('descriptions') as UntypedFormGroup;
    if (!descriptions) {
      return null;
    }

    let control = descriptions.get(language) as UntypedFormControl;
    if (!control && language) {
      control = new UntypedFormControl('', [Validators.required]);
      descriptions.addControl(language, control);
    }

    return control ?? null;
  }

  getCustomFeedbackControl(
    optionIndex: number,
    language: string
  ): UntypedFormControl | null {
    const optionGroup = this.ensureOptionGroup(optionIndex);
    if (!optionGroup) {
      return null;
    }

    let customFeedback = optionGroup.get('custom_feedback') as UntypedFormGroup;
    if (!customFeedback) {
      customFeedback = this.fb.group({});
      optionGroup.setControl('custom_feedback', customFeedback);
    }

    let control = customFeedback.get(language) as UntypedFormControl;
    if (!control && language) {
      control = new UntypedFormControl('');
      customFeedback.addControl(language, control);
    }

    return control ?? null;
  }

  private ensureOptionGroup(index: number): UntypedFormGroup | null {
    if (!this.options) {
      return null;
    }

    this.ensureOptionCount();

    const optionGroup = (this.options.at(index) as UntypedFormGroup) ?? null;
    if (optionGroup && !optionGroup.get('is_default_option')) {
      optionGroup.addControl(
        'is_default_option',
        new UntypedFormControl(false)
      );
    }
    if (optionGroup && !optionGroup.get('custom_feedback')) {
      optionGroup.addControl('custom_feedback', this.fb.group({}));
    }
    return optionGroup;
  }

  private ensureOptionCount(): void {
    if (!this.options) {
      return;
    }

    while (this.options.length < BRANCHING_OPTION_MIN) {
      this.options.push(this.createOptionGroup(this.options.length));
    }
  }

  private ensureDescriptions(): void {
    if (!this.options?.length) {
      return;
    }

    this.optionIndexes.forEach((index) => {
      const optionGroup = this.ensureOptionGroup(index);
      if (!optionGroup) {
        return;
      }

      let descriptions = optionGroup.get('descriptions') as UntypedFormGroup;
      if (!descriptions) {
        descriptions = this.fb.group({});
        optionGroup.setControl('descriptions', descriptions);
      }
      let customFeedback = optionGroup.get('custom_feedback') as UntypedFormGroup;
      if (!customFeedback) {
        customFeedback = this.fb.group({});
        optionGroup.setControl('custom_feedback', customFeedback);
      }

      const existingLanguages = Object.keys(descriptions.controls);
      existingLanguages.forEach((lang) => {
        if (this.languages.length && !this.languages.includes(lang)) {
          descriptions.removeControl(lang);
        }
      });
      const existingFeedbackLanguages = Object.keys(customFeedback.controls);
      existingFeedbackLanguages.forEach((lang) => {
        if (this.languages.length && !this.languages.includes(lang)) {
          customFeedback.removeControl(lang);
        }
      });

      this.languages.forEach((lang) => {
        if (!descriptions.get(lang)) {
          descriptions.addControl(
            lang,
            new UntypedFormControl('', [Validators.required])
          );
        }
        if (!customFeedback.get(lang)) {
          customFeedback.addControl(lang, new UntypedFormControl(''));
        }
      });
    });
  }

  private createOptionGroup(order: number): UntypedFormGroup {
    const descriptionsGroup = this.fb.group({});
    const customFeedbackGroup = this.fb.group({});

    (this.languages ?? []).forEach((lang) => {
      descriptionsGroup.addControl(
        lang,
        new UntypedFormControl('', [Validators.required])
      );
      customFeedbackGroup.addControl(lang, new UntypedFormControl(''));
    });

    return this.fb.group({
      id: [''],
      session_item_id: [''],
      is_default_option: [false],
      descriptions: descriptionsGroup,
      custom_feedback: customFeedbackGroup,
      order: [order],
    });
  }

  hasError(optionIndex: number, language: string): boolean {
    const control = this.getDescriptionControl(optionIndex, language);
    if (!control) {
      return false;
    }
    return (this.submitted || control.touched) && control.invalid;
  }

  private setupDefaultOptionListeners(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];

    if (!this.options) {
      return;
    }

    this.optionIndexes.forEach((index) => {
      const optionGroup = this.ensureOptionGroup(index);
      const defaultControl = optionGroup?.get(
        'is_default_option'
      ) as UntypedFormControl;
      if (defaultControl) {
        const sub = defaultControl.valueChanges.subscribe(
          (isChecked: boolean) => {
            if (isChecked) {
              this.optionIndexes.forEach((otherIndex) => {
                if (otherIndex !== index) {
                  const otherOptionGroup = this.ensureOptionGroup(otherIndex);
                  const otherDefaultControl = otherOptionGroup?.get(
                    'is_default_option'
                  ) as UntypedFormControl;
                  if (otherDefaultControl?.value) {
                    otherDefaultControl.setValue(false, { emitEvent: false });
                  }
                }
              });
            }
          }
        );
        this.subscriptions.push(sub);
      }
    });
  }
}
